import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, Send, Upload, CheckCircle2, Loader2 } from "lucide-react";
import { PageHeader } from "./PaintlessDentRemoval";
import { Reveal } from "../components/Shared";
import Seo from "../components/Seo";
import { useToast } from "../hooks/use-toast";
import { business } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", location: "", vehicle: "", message: "" });
  const [hp, setHp] = useState(""); // honeypot (should stay empty)
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const emailValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast({ title: "Please complete the form", description: "Name, email and a description are required." });
      return;
    }
    if (!emailValid(form.email)) {
      toast({ title: "Invalid email", description: "Please enter a valid email address." });
      return;
    }

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("message", form.message);
      fd.append("phone", form.phone);
      fd.append("location", form.location);
      fd.append("vehicle", form.vehicle);
      fd.append("hp_field", hp); // honeypot
      if (file) fd.append("photo", file);

      await axios.post(`${API}/enquiry`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Only clear/advance on success
      setSubmitted(true);
      toast({
        title: "Enquiry sent!",
        description: "Thanks! Your enquiry has been sent to Sam. We'll get back to you as soon as possible.",
      });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast({
          title: "Please wait a moment",
          description: err?.response?.data?.detail || "You've just sent an enquiry. Please try again shortly.",
        });
      } else {
        toast({
          title: "Something went wrong",
          description: "Please try again or call Sam on " + business.phone + ".",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Seo
        title="Contact Dentcraft NSW | Free Dent Repair Quote Sydney"
        description="Contact Dentcraft NSW for a free paintless dent removal quote in Sydney and Western Sydney. Text a photo of your dent to Sam on 0474 020 202 or use our online form."
        path="/contact"
      />
      <PageHeader
        title="Contact Us"
        crumb="Contact Us"
        image="https://images.unsplash.com/photo-1508974576580-36a2f92ad3bc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
      />

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-5 gap-12">
          {/* Info */}
          <Reveal className="lg:col-span-2">
            <span className="text-[#84bd00] font-semibold text-sm tracking-widest uppercase">Get In Touch</span>
            <h2 className="mt-3 font-display font-extrabold text-3xl text-[#242424]">
              Contact Sam for a free quotation
            </h2>
            <p className="mt-5 text-gray-600 leading-relaxed">
              Take a photo of your dent and text Sam on {business.phone} to get an on-the-spot estimate. Leave a brief description of the damaged area and we'll reply back within the same day.
            </p>
            <p className="mt-3 text-gray-600 leading-relaxed">
              Or complete the online form and attach a photo of the damage so we can provide a more accurate estimate.
            </p>

            <div className="mt-8 space-y-5">
              {[
                { icon: Phone, label: business.phone, href: `tel:${business.phoneRaw}` },
                { icon: Mail, label: business.email, href: `mailto:${business.email}` },
                { icon: MapPin, label: business.address },
                { icon: Clock, label: "Same-day replies • 7 days a week" },
              ].map((c, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="shrink-0 w-12 h-12 rounded-xl bg-[#84bd00]/10 flex items-center justify-center">
                    <c.icon size={22} className="text-[#84bd00]" />
                  </div>
                  {c.href ? (
                    <a href={c.href} className="text-[#242424] font-medium hover:text-[#84bd00] transition-colors pt-2.5">{c.label}</a>
                  ) : (
                    <span className="text-[#242424] font-medium pt-2.5">{c.label}</span>
                  )}
                </div>
              ))}
            </div>
          </Reveal>

          {/* Form */}
          <Reveal className="lg:col-span-3" delay={120}>
            <div className="bg-[#f7f9f2] rounded-2xl p-8 md:p-10">
              {submitted ? (
                <div className="text-center py-16">
                  <div className="mx-auto w-20 h-20 rounded-full bg-[#84bd00] flex items-center justify-center mb-6">
                    <CheckCircle2 size={40} className="text-white" />
                  </div>
                  <h3 className="font-display font-extrabold text-2xl text-[#242424] mb-2">Thank you!</h3>
                  <p className="text-gray-600">Your enquiry has been sent to Sam. We'll get back to you as soon as possible.</p>
                  <button onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", location: "", vehicle: "", message: "" }); setFile(null); setFileName(""); }} className="mt-8 text-[#84bd00] font-semibold hover:underline">
                    Send another enquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5">
                  {/* Honeypot field - hidden from users, catches bots. Named non-standard + autofill disabled so browsers never fill it. */}
                  <input
                    type="text"
                    name="hp_field"
                    value={hp}
                    onChange={(e) => setHp(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                  />
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Name" name="name" value={form.name} onChange={handle} placeholder="Your name" required />
                    <Field label="Email" name="email" type="email" value={form.email} onChange={handle} placeholder="you@email.com" required />
                    <Field label="Contact Phone" name="phone" value={form.phone} onChange={handle} placeholder="04xx xxx xxx" />
                    <Field label="Your Location" name="location" value={form.location} onChange={handle} placeholder="Suburb" />
                  </div>
                  <Field label="Make & Model of Vehicle" name="vehicle" value={form.vehicle} onChange={handle} placeholder="e.g. Toyota Corolla 2021" />
                  <div>
                    <label className="block text-sm font-semibold text-[#242424] mb-2">Describe the damage</label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handle}
                      rows={5}
                      required
                      placeholder="Please provide a description of the damage and where it is located on the vehicle."
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm outline-none focus:border-[#84bd00] focus:ring-2 focus:ring-[#84bd00]/20 transition-all bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#242424] mb-2">Attach a photo (optional, max 5MB)</label>
                    <label className="flex items-center gap-3 border border-dashed border-gray-300 rounded-lg px-4 py-3 cursor-pointer hover:border-[#84bd00] transition-colors bg-white">
                      <Upload size={18} className="text-[#84bd00]" />
                      <span className="text-sm text-gray-500 truncate">{fileName || "Choose a file"}</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files[0];
                          if (f && f.size > 5 * 1024 * 1024) {
                            toast({ title: "File too large", description: "Please choose an image under 5MB." });
                            return;
                          }
                          setFile(f || null);
                          setFileName(f?.name || "");
                        }}
                      />
                    </label>
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#84bd00] hover:bg-[#75a800] disabled:opacity-70 disabled:cursor-not-allowed text-white font-semibold px-6 py-4 rounded-lg transition-colors"
                  >
                    {loading ? (
                      <><Loader2 size={18} className="animate-spin" /> Sending...</>
                    ) : (
                      <><Send size={18} /> Send Enquiry</>
                    )}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, name, value, onChange, placeholder, type = "text", required }) => (
  <div>
    <label className="block text-sm font-semibold text-[#242424] mb-2">
      {label}{required && <span className="text-[#84bd00]"> *</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm outline-none focus:border-[#84bd00] focus:ring-2 focus:ring-[#84bd00]/20 transition-all bg-white"
    />
  </div>
);

export default Contact;
